import { AxiosError, AxiosResponse } from "axios";
import { Button, Container, Form, Stack, Text } from "basis";
import { parseSessionResponse } from "components/IncreaseCreditLimit/utils";
import { getHref, postHref } from "library/api/httpMethods";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { CreditCard } from "../../CreditCard/CreditCard";
import { OneTimeCode } from "../../OneTimeCode/OneTimeCode";
import { SvgInline } from "../../SvgInline/SvgInline";
import { ActionType, ScreenProps } from "../reducer/types";
import {
  PersonalDetailsResponse,
  SessionResponse,
  VerifyOTPResponse,
} from "../response/response";
import { extractPersonalDetails } from "../utils";

type SessionVariables = {
  createSessionUrl: string;
  tokenId: string;
};

const initialValues = {
  oneTimeCode: "",
};

export const Otp = ({ state, dispatch }: ScreenProps) => {
  const [otpState, setOtpSate] = useState<{
    otpVerifyUrl: string;
    otpResendUrl: string;
    maskedMobileNumber: string;
  } | null>(null);

  const { accountInfo, personalDetailsUnmaskedUrl } = state;

  const { refetch: personalDetailsQuery } = useQuery<
    PersonalDetailsResponse,
    AxiosError
  >(
    [personalDetailsUnmaskedUrl, { url: personalDetailsUnmaskedUrl }],
    async () => {
      const response = await getHref<PersonalDetailsResponse>(
        personalDetailsUnmaskedUrl,
      );
      return response.data;
    },
    {
      onSuccess(details) {
        dispatch({
          type: ActionType.OTP_SUCCESS,
          personalDetails: {
            ...extractPersonalDetails(details),
          },
        });
      },
      onError() {
        dispatch({ type: ActionType.OTP_FAILED });
      },
      enabled: false,
    },
  );

  const verifyMutation = useMutation<
    AxiosResponse<VerifyOTPResponse>,
    AxiosError,
    {
      oneTimeCode: string;
      otpVerifyUrl: string;
    }
  >(({ oneTimeCode, otpVerifyUrl }) =>
    postHref<VerifyOTPResponse>(otpVerifyUrl, {
      otp: oneTimeCode,
    }),
  );

  const sessionInfo = useMutation(
    async ({ createSessionUrl, tokenId }: SessionVariables) => {
      const response = await postHref<SessionResponse>(createSessionUrl, {
        tokenId,
      });
      const responseData = parseSessionResponse(response.data);

      if (responseData === null) {
        throw new window.Error("Invalid session response received");
      }

      return responseData;
    },
  );

  const createSession = (createSessionUrl: string) => {
    if (accountInfo === null) {
      return;
    }

    sessionInfo.mutate(
      {
        createSessionUrl,
        tokenId: accountInfo.cardToken,
      },
      {
        onSuccess: ({ otpResendUrl, otpVerifyUrl, maskedMobileNumber }) => {
          setOtpSate({
            otpResendUrl,
            otpVerifyUrl,
            maskedMobileNumber,
          });
        },
        onError: () => {
          dispatch({ type: ActionType.OTP_FAILED });
        },
      },
    );
  };

  useEffect(() => {
    createSession(state.createSessionUrl as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async ({ errors, values, setErrors }) => {
    if (otpState && Object.keys(errors).length === 0) {
      verifyMutation.mutate(
        { ...values, otpVerifyUrl: otpState.otpVerifyUrl },
        {
          onSuccess: (response) => {
            switch (response.data.verification_outcome) {
              case "OTP_Mismatch": {
                setErrors({
                  oneTimeCode: "Invalid code",
                });
                break;
              }

              case "OTP_OK": {
                personalDetailsQuery();
                break;
              }

              default: {
                dispatch({
                  type: ActionType.OTP_FAILED,
                });
              }
            }
          },
        },
      );
    }
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      <Stack gap="8">
        <Container bg="grey.t05" padding="4">
          <CreditCard
            cardName={accountInfo.cardName}
            cardEndsWith={accountInfo.cardEndsWith}
          />
        </Container>
        <SvgInline name="icon-security-lock" size="92" />
        <Text as="h3" textStyle="heading5" textStyle-sm="heading4">
          Verify your identity
        </Text>
        <OneTimeCode
          otpRecipient={otpState?.maskedMobileNumber || ""}
          resendMutationFn={() => postHref(otpState?.otpResendUrl || "")}
          onResendCodeError={() => {
            dispatch({ type: ActionType.OTP_FAILED });
          }}
        />
        <Button type="submit" width="160" loading={verifyMutation.isLoading}>
          Next
        </Button>
      </Stack>
    </Form>
  );
};
